import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { CategoriaMinuta, ITipoMinuta } from '../modelo';
import { IIndicadores } from '../modelo/Indicadores';
import { MotivosImpedimento } from '../modelo/MotivosImpedimento';
import { IParametroSistema, PARAMETRO_UNIDADES_ESCANINHO_PAUTA, VALORES_TODAS_UNIDADES } from '../modelo/ParametroSistema';
import { ISituacaoMinuta } from '../modelo/SituacaoMinuta';
import { ISituacaoPeticao } from '../modelo/SituacaoPeticao';
import { ITipoControleGabinete } from '../modelo/TipoControleGabinete';
import { ITipoPeticaoPendente } from '../modelo/TipoPeticaoPendente';
import { OrgaoJulgadorDto } from '../modelo/dto/OrgaoJulgadorDto';
import ControleGabineteService from '../services/ControleGabineteService';
import IndicadoresService from '../services/IndicadoresService';
import MinutaService from '../services/MinutaService';
import UsuarioStore from './UsuarioStore';
import { Minuta, Processo } from '../modelo';
import ParametroSistemaService from '../services/ParametroSistemaService';
import StoreParaSingleton from './StoreParaSingleton';

class ParametroStore extends StoreParaSingleton {
    tiposMinuta: Array<ITipoMinuta> = [];
    situacoesMinuta: Array<ISituacaoMinuta> = [];
    situacoesPeticao: Array<ISituacaoPeticao> = [];
    tiposControle: Array<ITipoControleGabinete> = [];
    orgaosJulgadores: Array<OrgaoJulgadorDto> = [];
    indicadores: Array<IIndicadores> = [];
    categorias: Array<CategoriaMinuta> = [];
    motivosImpedimentos: Array<MotivosImpedimento> = [];
    tiposPeticaoPendente: Array<ITipoPeticaoPendente> = [];
    parametrosCarregados: boolean = false;
    parametrosSistema: Array<IParametroSistema> = [];
    visualizacaoCompacta: boolean = localStorage.getItem('visualizacaoCompacta') === 'true';
    openPopoverMinuta: boolean = false;
    openPopoverProcesso: boolean = false;
    anchorEl = null;
    tipo = '';
    processoPopover: Processo | null = null;
    minutaPopover: Minuta | null = null;
    numMinutaPopover: string | null = null;
    idProcessoPopover: number | null = null;
    isAlertaSistemaHabilitado: boolean = false;

    constructor() {
        // TODO: [mobx-undecorate] verify the constructor arguments and the arguments of this automatically generated super call
        super();

        makeObservable(this, {
            tiposMinuta: observable,
            situacoesMinuta: observable,
            situacoesPeticao: observable,
            tiposControle: observable,
            orgaosJulgadores: observable,
            indicadores: observable,
            categorias: observable,
            motivosImpedimentos: observable,
            tiposPeticaoPendente: observable,
            parametrosCarregados: observable,
            parametrosSistema: observable,
            visualizacaoCompacta: observable,
            openPopoverMinuta: observable,
            openPopoverProcesso: observable,
            anchorEl: observable,
            tipo: observable,
            processoPopover: observable,
            minutaPopover: observable,
            numMinutaPopover: observable,
            idProcessoPopover: observable,
            isAlertaSistemaHabilitado: observable,
            objetoSave: computed,
            inicializar: action,
            limpar: action,
            fazerImportacaoAutomatica: computed,
            unidadesComEscaninhoPauta: computed,
            escaninhoPautaHabilitado: computed,
            openPopoverMinutaCompacta: action,
            openPopoverProcessoCompacto: action,
            toggleVisualizacaoCompacta: action
        });
    }

    get objetoSave() {
        return {
            nomeStore: 'ParametroStore',
            tiposMinuta: this.tiposMinuta,
            situacoesMinuta: this.situacoesMinuta,
            situacoesPeticao: this.situacoesPeticao,
            tiposControle: this.tiposControle,
            orgaosJulgadores: this.orgaosJulgadores,
            indicadores: this.indicadores,
            motivosImpedimentos: this.motivosImpedimentos,
            tiposPeticaoPendente: this.tiposPeticaoPendente
        };
    }

    inicializar = async () => {

        const [tiposMinutas, situacoesMinutas, situacoesPeticao, tiposControles, indicadores, tiposPeticaoPendente, impedimentos, parametros] = await Promise.all([
            MinutaService.getTiposMinuta(),
            MinutaService.getSituacoesMinuta(),
            MinutaService.getSituacoesPeticao(),
            ControleGabineteService.getTiposControles(),
            IndicadoresService.recuperarTodos(),
            ParametroSistemaService.recuperaTiposPeticoesPendentes(),
            ParametroSistemaService.recuperaTiposImpedimentos(),
            ParametroSistemaService.todosParametros(),
        ])

        runInAction(() => {
            const indicadoresUnicos: IIndicadores[] = Object.values(
                indicadores.reduce((r, o) => {
                    if (!r[o.descricao]) {
                        r[o.descricao] = o;
                    }
                    return r;
                }, {})
            );
            indicadoresUnicos.sort((a, b) => (a.descricao > b.descricao) ? 1 : ((b.descricao > a.descricao) ? -1 : 0));

            this.tiposMinuta = tiposMinutas;
            this.situacoesMinuta = situacoesMinutas;
            this.situacoesPeticao = situacoesPeticao;
            this.tiposControle = tiposControles;
            this.indicadores = indicadoresUnicos;
            this.tiposPeticaoPendente = tiposPeticaoPendente;
            this.parametrosCarregados = true;
            this.motivosImpedimentos = impedimentos;
            this.parametrosSistema = parametros;
        });
    };
    limpar = () => {
    };

    get fazerImportacaoAutomatica() {
        const parametroImportacaoAutomatica = this.parametrosSistema.find(param => param.nome === 'GabsImportacaoAutomatica');
        return parametroImportacaoAutomatica?.valor.includes(UsuarioStore.gabinete);
    }

    get unidadesComEscaninhoPauta(): string[] {
        const parametro = this.parametrosSistema.find(param => param.nome === PARAMETRO_UNIDADES_ESCANINHO_PAUTA);
        return (parametro?.valor || '')
            .split(/[,;]/)
            .map(unidade => unidade.trim().toUpperCase())
            .filter(unidade => unidade.length > 0);
    }

    /**
     * Enquanto o escaninho de pauta não é difundido para todos, apenas as unidades
     * listadas no parâmetro de sistema visualizam e acessam o escaninho.
     */
    get escaninhoPautaHabilitado() {
        if (!this.parametrosCarregados) {
            return false;
        }
        const unidades = this.unidadesComEscaninhoPauta;
        if (unidades.some(unidade => VALORES_TODAS_UNIDADES.includes(unidade))) {
            return true;
        }
        const gabinete = (UsuarioStore.gabinete || '').trim().toUpperCase();
        return !!gabinete && unidades.includes(gabinete);
    }

    openPopoverMinutaCompacta = (anchorEl: HTMLElement, minuta: Minuta, processo: Processo) => {
        this.anchorEl = anchorEl;
        this.minutaPopover = minuta;
        this.processoPopover = processo;
        this.openPopoverMinuta = true;
    };

    openPopoverProcessoCompacto(anchorEl: HTMLElement, processo: Processo) {
        this.anchorEl = anchorEl;
        this.processoPopover = processo;
        this.openPopoverProcesso = true;
    }

    toggleVisualizacaoCompacta = () => {
        this.visualizacaoCompacta = !this.visualizacaoCompacta;
        localStorage.setItem('visualizacaoCompacta', this.visualizacaoCompacta.toString());
    };
}

export default new ParametroStore();
